import React, { useEffect, useMemo, useState } from 'react';
import { clinicService, onboardingService } from '../services/api';

const OnboardingTokens = () => {
  const [clinics, setClinics] = useState([]);
  const [clinicPage, setClinicPage] = useState(1);
  const [hasMoreClinics, setHasMoreClinics] = useState(false);
  const [loadingMoreClinics, setLoadingMoreClinics] = useState(false);
  const [selectedClid, setSelectedClid] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [locale, setLocale] = useState('en_US');
  const [integration, setIntegration] = useState('');
  const [referrer, setReferrer] = useState('none');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokens, setTokens] = useState([]);
  const [expandedTokenId, setExpandedTokenId] = useState(null);

  // Start WhatsApp onboarding (POST /api/onboarding/start-conversation):
  //   - registers nexi_onboarding_questionnaire.owner_phone <-> clid mapping
  //   - sends nexi_onboarding_greeting from the central onboarding number
  // The form is shown inline under a single token row at a time.
  const [startFormTokenId, setStartFormTokenId] = useState(null);
  const [startOwnerPhone, setStartOwnerPhone] = useState('');
  const [startLocale, setStartLocale] = useState('');
  const [startSubmitting, setStartSubmitting] = useState(false);
  const [startError, setStartError] = useState('');
  const [startSuccess, setStartSuccess] = useState(null);

  // Create organization with clinic and token
  const [orgName, setOrgName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [orgClid, setOrgClid] = useState('');
  const [orgOrganizationId, setOrgOrganizationId] = useState('');
  const [orgRegion, setOrgRegion] = useState('HK');
  const [orgContactNumber, setOrgContactNumber] = useState('');
  const [orgLocale, setOrgLocale] = useState('en_US');
  const [orgIntegration, setOrgIntegration] = useState('');
  const [orgReferrer, setOrgReferrer] = useState('none');
  const [createQuotaWallet, setCreateQuotaWallet] = useState(true);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [orgSuccess, setOrgSuccess] = useState(null);

  const selectedClinic = useMemo(
    () => clinics.find((clinic) => clinic.id === selectedClid) || null,
    [clinics, selectedClid]
  );

  const loadClinics = async () => {
    try {
      const response = await clinicService.getClinics(
        1,
        '',
        'setup,reviewing,ready,published'
      );
      const items = response?.clinics?.items || [];
      setClinics(items);
      setClinicPage(1);
      setHasMoreClinics((response?.clinics?.page ?? 1) < (response?.clinics?.pages ?? 0));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load clinics');
    }
  };

  const loadMoreClinics = async () => {
    if (!hasMoreClinics || loadingMoreClinics) return;
    setLoadingMoreClinics(true);
    setError('');
    try {
      const nextPage = clinicPage + 1;
      const response = await clinicService.getClinics(
        nextPage,
        '',
        'setup,reviewing,ready,published'
      );
      const items = response?.clinics?.items || [];
      setClinics((prev) => [...prev, ...items]);
      setClinicPage(nextPage);
      setHasMoreClinics((response?.clinics?.page ?? 1) < (response?.clinics?.pages ?? 0));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load more clinics');
    } finally {
      setLoadingMoreClinics(false);
    }
  };

  const loadTokens = async (clid = null) => {
    setLoading(true);
    setError('');
    try {
      const response = await onboardingService.listOnboardingTokens({
        clid: clid || null,
        limit: 200,
      });
      setTokens(response.items || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load onboarding tokens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClinics();
    loadTokens();
  }, []);

  const handleClinicChange = (event) => {
    const clid = event.target.value;
    if (clid === '__load_more__') {
      loadMoreClinics();
      return;
    }
    setSelectedClid(clid);
    const clinic = clinics.find((item) => item.id === clid);
    setDisplayName(clinic?.name || '');
    setLocale(clinic?.region === 'HK' ? 'zh_HK' : 'en_US');
  };

  const handleCreate = async () => {
    if (!selectedClid) {
      setError('Please select a clinic');
      return;
    }
    if (!displayName.trim()) {
      setError('Please provide display name');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await onboardingService.createOnboardingToken({
        clid: selectedClid,
        name: displayName.trim(),
        locale,
        type: 'clinic',
        integration: integration || null,
        referrer: referrer === 'none' ? null : referrer,
      });
      await loadTokens(selectedClid);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create onboarding token');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard');
    } catch (_err) {
      setError('Failed to copy to clipboard');
    }
  };

  const openStartForm = (token) => {
    setStartFormTokenId(token.id);
    setStartOwnerPhone(token.owner_phone || '');
    setStartLocale(token.locale || '');
    setStartError('');
    setStartSuccess(null);
  };

  const closeStartForm = () => {
    setStartFormTokenId(null);
    setStartOwnerPhone('');
    setStartLocale('');
    setStartError('');
    setStartSuccess(null);
  };

  const handleStartConversation = async (token) => {
    const phone = startOwnerPhone.trim();
    if (!phone) {
      setStartError('Owner phone is required (E.164, e.g. +85291234567).');
      return;
    }
    if (!phone.startsWith('+')) {
      setStartError('Owner phone must be in E.164 format (e.g. +85291234567).');
      return;
    }
    setStartSubmitting(true);
    setStartError('');
    setStartSuccess(null);
    try {
      const response = await onboardingService.startOnboardingConversation({
        token: token.token,
        ownerPhone: phone,
        locale: startLocale || null,
      });
      setStartSuccess(response);
      await loadTokens(selectedClid || null);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to start onboarding conversation';
      setStartError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setStartSubmitting(false);
    }
  };

  const handleOrgClidChange = (event) => {
    const value = event.target.value;
    const previousClid = orgClid.trim();
    const previousSuggestedOrgId = previousClid ? `org_${previousClid}` : '';
    setOrgClid(value);
    setOrgOrganizationId((previousOrgId) => {
      const trimmedPrevious = previousOrgId.trim();
      const nextSuggestedOrgId = value.trim() ? `org_${value.trim()}` : '';
      if (!trimmedPrevious || trimmedPrevious === previousSuggestedOrgId) {
        return nextSuggestedOrgId;
      }
      return previousOrgId;
    });
  };

  const handleCreateOrganizationWithClinic = async () => {
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }
    if (!clinicName.trim()) {
      setError('Clinic name is required');
      return;
    }
    const trimmedClid = orgClid.trim();
    const trimmedOrganizationId = orgOrganizationId.trim();
    const resolvedOrganizationId = trimmedOrganizationId || (trimmedClid ? `org_${trimmedClid}` : '');
    if (!resolvedOrganizationId) {
      setError('Organization ID is required (or provide a CLID to derive org_{CLID})');
      return;
    }
    setCreatingOrg(true);
    setError('');
    setOrgSuccess(null);
    try {
      const response = await onboardingService.createOrganizationWithClinic({
        organizationName: orgName.trim(),
        clinicName: clinicName.trim(),
        clid: trimmedClid || undefined,
        organizationId: resolvedOrganizationId,
        region: orgRegion,
        contactNumber: orgContactNumber.trim(),
        locale: orgLocale,
        integration: orgIntegration || undefined,
        referrer: orgReferrer === 'none' ? null : orgReferrer,
        createQuotaWallet,
      });
      setOrgSuccess(response);
      loadTokens();
      loadClinics();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create organization with clinic');
    } finally {
      setCreatingOrg(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-semibold text-gray-800">Onboarding Tokens</h1>
        <p className="mt-1 text-sm text-gray-600">
          Create onboarding links and view saved answers submitted from token onboarding flow.
        </p>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 rounded-lg border border-gray-200 p-4">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Create organization with clinic and token</h2>
          <p className="text-sm text-gray-600 mb-4">
            Create a new organization, its first clinic, and an onboarding link in one step. Set the Organization ID here — clinics use it at hub login. When CLID is set, Organization ID is prefilled as <code className="text-xs bg-gray-100 px-1 rounded">org_&#123;CLID&#125;</code> (editable). Optionally create a quota wallet for the clinic.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization name</label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                placeholder="e.g. Acme Medical Group"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
              <input
                type="text"
                value={orgOrganizationId}
                onChange={(e) => setOrgOrganizationId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                placeholder={orgClid.trim() ? `org_${orgClid.trim()}` : 'e.g. org_SGCL0010'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {orgClid.trim()
                  ? `Suggested: org_${orgClid.trim()}`
                  : 'Enter CLID below to auto-suggest, or type an ID (e.g. org_SGCL0010)'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clinic name</label>
              <input
                type="text"
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                placeholder="e.g. Central Branch"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CLID (optional)</label>
              <input
                type="text"
                value={orgClid}
                onChange={handleOrgClidChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                placeholder="Leave empty to auto-generate"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
              <select
                value={orgRegion}
                onChange={(e) => setOrgRegion(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="HK">HK</option>
                <option value="SG">SG</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact number (optional)</label>
              <input
                type="text"
                value={orgContactNumber}
                onChange={(e) => setOrgContactNumber(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                placeholder=""
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Locale</label>
              <select
                value={orgLocale}
                onChange={(e) => setOrgLocale(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="en_US">en_US</option>
                <option value="zh_HK">zh_HK</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Integration</label>
              <select
                value={orgIntegration}
                onChange={(e) => setOrgIntegration(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="">None</option>
                <option value="sg_clinic_assist">sg_clinic_assist</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referrer (optional)</label>
              <select
                value={orgReferrer}
                onChange={(event) => setOrgReferrer(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="none">none</option>
                <option value="hkt">hkt</option>
                <option value="hkbn">hkbn</option>
                <option value="sg_clinic_assist">sg_clinic_assist</option>
                <option value="sgimed">sgimed</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="createQuotaWallet"
                checked={createQuotaWallet}
                onChange={(e) => setCreateQuotaWallet(e.target.checked)}
                className="rounded border-gray-300 text-[#075e54] focus:ring-[#075e54]"
              />
              <label htmlFor="createQuotaWallet" className="text-sm text-gray-700">
                Create quota wallet
              </label>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCreateOrganizationWithClinic}
                disabled={creatingOrg}
                className="w-full rounded-md bg-[#075e54] px-4 py-2 text-white text-sm font-medium hover:bg-[#064c43] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {creatingOrg ? 'Creating...' : 'Create organization & token'}
              </button>
            </div>
          </div>
          {orgSuccess && (
            <div className="mt-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
              <p className="font-medium">Created successfully.</p>
              <p className="mt-1">Organization ID: {orgSuccess.organization_id}</p>
              <p className="mt-1">Clinic ID (CLID): {orgSuccess.clid}</p>
              <p className="mt-2">
                <span className="font-medium">Onboarding URL:</span>{' '}
                <span className="break-all">{orgSuccess.onboarding_url}</span>
              </p>
              <button
                onClick={() => copyToClipboard(orgSuccess.onboarding_url)}
                className="mt-2 rounded border border-green-300 bg-white px-2 py-1 text-xs hover:bg-green-50"
              >
                Copy URL
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 rounded-lg border border-gray-200 p-4">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Create onboarding token (existing clinic)</h2>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clinic</label>
              <select
                value={selectedClid}
                onChange={handleClinicChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="">Select clinic</option>
                {clinics.map((clinic) => (
                  <option key={clinic.id} value={clinic.id}>
                    {clinic.id} - {clinic.name}
                  </option>
                ))}
                {hasMoreClinics && (
                  <option value="__load_more__" disabled={loadingMoreClinics}>
                    {loadingMoreClinics ? 'Loading...' : 'Load more'}
                  </option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                placeholder="Clinic display name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Locale</label>
              <select
                value={locale}
                onChange={(event) => setLocale(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="en_US">en_US</option>
                <option value="zh_HK">zh_HK</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Integration Type</label>
              <select
                value={integration}
                onChange={(event) => setIntegration(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="">None</option>
                <option value="sg_clinic_assist">sg_clinic_assist</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referrer (optional)</label>
              <select
                value={referrer}
                onChange={(event) => setReferrer(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
              >
                <option value="none">none</option>
                <option value="hkt">hkt</option>
                <option value="hkbn">hkbn</option>
                <option value="sg_clinic_assist">sg_clinic_assist</option>
                <option value="sgimed">sgimed</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full rounded-md bg-[#075e54] px-4 py-2 text-white text-sm font-medium hover:bg-[#064c43] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create Token'}
              </button>
            </div>
          </div>
          {selectedClinic && (
            <p className="mt-3 text-xs text-gray-500">
              Region: {selectedClinic.region || 'N/A'}
            </p>
          )}
        </div>

        <div className="mt-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-lg font-medium text-gray-800">Token list and answers</h2>
            <button
              onClick={() => loadTokens(selectedClid || null)}
              disabled={loading}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">CLID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Token</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Step</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Locale</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Owner Phone</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Integration Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Referrer</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {tokens.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.clid}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 max-w-xs truncate">{item.token}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.current_step}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.locale}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {item.owner_phone ? (
                          <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            {item.owner_phone}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">not set</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.integration || 'None'}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 max-w-xs truncate">{item.referrer || '—'}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => copyToClipboard(item.token)}
                            className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                          >
                            Copy Token
                          </button>
                          <button
                            onClick={() => copyToClipboard(item.onboarding_url)}
                            className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                          >
                            Copy URL
                          </button>
                          <button
                            onClick={() =>
                              setExpandedTokenId(expandedTokenId === item.id ? null : item.id)
                            }
                            className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                          >
                            {expandedTokenId === item.id ? 'Hide Answers' : 'Show Answers'}
                          </button>
                          {item.current_step !== 'complete' && (
                            <button
                              onClick={() =>
                                startFormTokenId === item.id ? closeStartForm() : openStartForm(item)
                              }
                              disabled={startSubmitting && startFormTokenId === item.id}
                              className="rounded bg-[#075e54] px-2 py-1 text-xs font-medium text-white hover:bg-[#064c43] disabled:opacity-60"
                              title={
                                item.owner_phone
                                  ? 'Re-send greeting / update owner phone'
                                  : 'Register owner phone and send greeting'
                              }
                            >
                              {item.owner_phone
                                ? startFormTokenId === item.id
                                  ? 'Cancel'
                                  : 'Resend Greeting'
                                : startFormTokenId === item.id
                                  ? 'Cancel'
                                  : 'Start WhatsApp'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {startFormTokenId === item.id && (
                      <tr>
                        <td colSpan={9} className="px-4 py-3 bg-emerald-50/40 border-y border-emerald-100">
                          <div className="flex flex-col gap-3">
                            <div className="text-xs text-gray-700">
                              Registers <code className="rounded bg-white px-1 border border-gray-200">owner_phone</code> &rarr;{' '}
                              <code className="rounded bg-white px-1 border border-gray-200">{item.clid}</code> on{' '}
                              <code className="rounded bg-white px-1 border border-gray-200">nexi_onboarding_questionnaire</code> and sends{' '}
                              <code className="rounded bg-white px-1 border border-gray-200">nexi_onboarding_greeting</code> from the central onboarding number.
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                              <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Owner phone (E.164)
                                </label>
                                <input
                                  type="text"
                                  value={startOwnerPhone}
                                  onChange={(e) => setStartOwnerPhone(e.target.value)}
                                  placeholder="+85291234567"
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Locale</label>
                                <select
                                  value={startLocale}
                                  onChange={(e) => setStartLocale(e.target.value)}
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075e54] focus:outline-none"
                                >
                                  <option value="">Use token default ({item.locale})</option>
                                  <option value="en_US">en_US</option>
                                  <option value="zh_HK">zh_HK</option>
                                  <option value="zh_CN">zh_CN</option>
                                </select>
                              </div>
                              <div className="flex items-end">
                                <button
                                  onClick={() => handleStartConversation(item)}
                                  disabled={startSubmitting}
                                  className="w-full rounded-md bg-[#075e54] px-4 py-2 text-white text-sm font-medium hover:bg-[#064c43] disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  {startSubmitting ? 'Sending...' : 'Send onboarding greeting'}
                                </button>
                              </div>
                            </div>
                            {startError && (
                              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                                {startError}
                              </div>
                            )}
                            {startSuccess && startSuccess.success && (
                              <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
                                <p className="font-medium">Greeting sent.</p>
                                <p className="mt-1">
                                  conversation_id:{' '}
                                  <code className="break-all">{startSuccess.conversation_id}</code>
                                </p>
                                {startSuccess.external_message_id && (
                                  <p className="mt-1">
                                    external_message_id:{' '}
                                    <code className="break-all">{startSuccess.external_message_id}</code>
                                  </p>
                                )}
                                {startSuccess.locale && (
                                  <p className="mt-1">locale: {startSuccess.locale}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedTokenId === item.id && (
                      <tr>
                        <td colSpan={9} className="px-4 py-3 bg-gray-50">
                          <div className="text-xs text-gray-600 mb-2">
                            <span className="font-medium">Onboarding URL:</span>{' '}
                            <span className="break-all">{item.onboarding_url}</span>
                          </div>
                          <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-x-auto">
                            {JSON.stringify(item.answers || {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {!loading && tokens.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                      No onboarding tokens found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTokens;
